import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChatMessageSelectableCopyArea from '../../common/chat/timeline/ChatMessageSelectableCopyArea';
import useViewportBoundedMenuPosition from '../../common/chat/shared/useViewportBoundedMenuPosition';
import { supportsChatMessageReaction } from '../../common/chat/reactions/chatMessageReactionUtils';
import {
  hasCopyableChatMessageText,
  resolveCopyableChatMessageText,
  writeTextToClipboard,
} from '../../common/chat/timeline/chatMessageCopyUtils';
import {
  canUnsendGroupChatMessage,
} from '../../common/chat/timeline/groupChatMessageUtils';
import { removeMessages } from '../../common/chat/timeline/timelineMerge';
import { deleteArtistGroupChatMessage } from '../../../api/artistChat';
import { pickFirstString } from './artistGroupChatPaneUtils';

export default function useArtistGroupChatMessageActions({
  chatTopics,
  announcementActionMessageId,
  artistUser,
  isAnnouncedMessage,
  loadThread,
  messagesContainerRef,
  onThreadsRefresh,
  pinAnnouncement,
  setMessages,
  setReplyDraft,
  threadId,
  toastError,
  toastSuccess,
  toggleReaction,
  unpinAnnouncement,
}) {
  const [messageMenu, setMessageMenu] = useState(null);
  const [inlineCopyMessageId, setInlineCopyMessageId] = useState('');
  const [unsendingMessageId, setUnsendingMessageId] = useState('');
  const longPressTimerRef = useRef(null);

  const closeMessageMenu = useCallback(() => {
    setMessageMenu(null);
  }, []);

  const closePartialCopyMode = useCallback(() => {
    setInlineCopyMessageId('');
  }, []);

  const canAnnouncementMessage = useCallback((message) => {
    const format = String(message?.message_format || '').trim();
    return format === 'text' || format === 'image' || format === 'file';
  }, []);

  const canUnsendMessage = useCallback(
    (message) =>
      canUnsendGroupChatMessage({
        message,
        viewerRole: 'artist',
        currentArtistId: artistUser?.userId,
      }),
    [artistUser?.userId]
  );

  const canReactMessage = useCallback((message) => {
    if (!message || !supportsChatMessageReaction(message)) return false;
    return !(
      String(message?.sender_type || '').trim().toLowerCase() === 'artist' &&
      String(message?.artist_user_id || '').trim() === String(artistUser?.userId || '').trim()
    );
  }, [artistUser?.userId]);

  const canReplyMessage = useCallback((message) => {
    if (!message) return false;
    const senderType = String(message.sender_type || '').trim().toLowerCase();
    const format = String(message.message_format || '').trim().toLowerCase();
    return ['admin', 'artist'].includes(senderType) && ['text', 'image', 'file'].includes(format);
  }, []);

  const canCopyMessage = useCallback((message) => hasCopyableChatMessageText(message), []);

  const canOpenMessageMenu = useCallback(
    (message) =>
      canCopyMessage(message) ||
      canReplyMessage(message) ||
      canUnsendMessage(message) ||
      canAnnouncementMessage(message) ||
      canReactMessage(message),
    [canAnnouncementMessage, canCopyMessage, canReactMessage, canReplyMessage, canUnsendMessage]
  );

  const { menuRef: messageMenuRef, resolveMenuPosition } = useViewportBoundedMenuPosition({
    menu: messageMenu,
    setMenu: setMessageMenu,
    padding: 12,
    fallbackWidth: 248,
    fallbackHeight: 360,
  });

  const clearMessageLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleMessageContextMenu = useCallback(
    (event, message) => {
      if (!canOpenMessageMenu(message)) return;
      event.preventDefault();
      const pos = resolveMenuPosition(event.clientX, event.clientY);
      setMessageMenu({ x: pos.x, y: pos.y, messageId: message.id, message });
    },
    [canOpenMessageMenu, resolveMenuPosition]
  );

  const handleMessageTouchStart = useCallback(
    (event, message) => {
      if (!canOpenMessageMenu(message)) return;
      clearMessageLongPress();
      const touch = event.touches?.[0];
      if (!touch) return;
      const { clientX, clientY } = touch;
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        const pos = resolveMenuPosition(clientX, clientY);
        setMessageMenu({ x: pos.x, y: pos.y, messageId: message.id, message });
      }, 550);
    },
    [canOpenMessageMenu, clearMessageLongPress, resolveMenuPosition]
  );

  const handleUnsendMessage = useCallback(async () => {
    const messageId = messageMenu?.messageId ? String(messageMenu.messageId) : '';
    if (!threadId || !messageId || unsendingMessageId) return;
    setUnsendingMessageId(messageId);
    try {
      const data = await deleteArtistGroupChatMessage(threadId, messageId);
      setMessageMenu(null);
      const deletedIds = Array.isArray(data?.messageIds) && data.messageIds.length
        ? data.messageIds.map((id) => String(id))
        : [messageId];
      setMessages((prev) => removeMessages(prev, deletedIds));
      await Promise.all([loadThread(), onThreadsRefresh ? onThreadsRefresh(true) : Promise.resolve()]);
    } catch (error) {
      toastError(error?.message || '送信取り消しに失敗しました');
    } finally {
      setUnsendingMessageId('');
    }
  }, [loadThread, messageMenu?.messageId, onThreadsRefresh, setMessages, threadId, toastError, unsendingMessageId]);

  const handleToggleAnnouncementMessage = useCallback(async () => {
    const messageId = messageMenu?.messageId ? String(messageMenu.messageId) : '';
    if (!messageId || announcementActionMessageId) return;
    setMessageMenu(null);
    try {
      if (isAnnouncedMessage(messageId)) {
        await unpinAnnouncement(messageId);
      } else {
        await pinAnnouncement(messageId);
      }
    } catch (_) {}
  }, [announcementActionMessageId, isAnnouncedMessage, messageMenu?.messageId, pinAnnouncement, unpinAnnouncement]);

  const handleMessageMenuReactionSelect = useCallback(async (messageId, reactionType) => {
    setMessageMenu(null);
    try {
      await toggleReaction(messageId, reactionType);
    } catch (_) {}
  }, [toggleReaction]);

  const handleCopyMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    setMessageMenu(null);
    if (!copyText) {
      toastError('コピーできる本文がありません。');
      return;
    }
    try {
      await writeTextToClipboard(copyText);
      toastSuccess('メッセージをコピーしました');
    } catch (error) {
      toastError(error?.message || 'コピーに失敗しました');
    }
  }, [messageMenu, toastError, toastSuccess]);

  const handleOpenInlinePartialCopy = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    setMessageMenu(null);
    if (!copyText) {
      toastError('コピーできる本文がありません。');
      return;
    }
    const nextMessageId = String(target.id || '').trim();
    setInlineCopyMessageId((current) => (current === nextMessageId ? '' : nextMessageId));
  }, [messageMenu, toastError]);

  const handleCopySelectionText = useCallback(async (selectedText) => {
    if (!String(selectedText || '').trim()) {
      toastError('コピーしたい範囲を選択してください。');
      return;
    }
    try {
      await writeTextToClipboard(selectedText);
      toastSuccess('選択範囲をコピーしました');
    } catch (error) {
      toastError(error?.message || 'コピーに失敗しました');
    }
  }, [toastError, toastSuccess]);

  const renderPartialCopyContent = useCallback((message, contentNode, isSelfMessage = false) => {
    const normalizedMessageId = String(message?.id || '').trim();
    if (!normalizedMessageId || !canCopyMessage(message)) return contentNode;
    return (
      <ChatMessageSelectableCopyArea
        active={inlineCopyMessageId === normalizedMessageId}
        messageId={normalizedMessageId}
        copyButtonSide={isSelfMessage ? 'left' : 'right'}
        onDeactivate={closePartialCopyMode}
        onCopySelection={handleCopySelectionText}
      >
        {contentNode}
      </ChatMessageSelectableCopyArea>
    );
  }, [canCopyMessage, closePartialCopyMode, handleCopySelectionText, inlineCopyMessageId]);

  const buildReplyDraftFromMessage = useCallback((message) => {
    if (!message) return null;
    const format = String(message.message_format || '').trim().toLowerCase();
    return {
      messageId: String(message.id || '').trim(),
      senderName:
        String(message.sender_type || '').trim().toLowerCase() === 'admin'
          ? pickFirstString(message.admin_display_name, message.admin_full_name, message.admin_username, '管理者')
          : String(message.artist_name || message.artist_name_en || artistUser?.name || artistUser?.username || 'アーティスト').trim(),
      type: format,
      previewText: format === 'text' ? String(message.content || '').trim() : '',
      fileName:
        format === 'file'
          ? String(message.original_name || message.file_file_name || '').trim()
          : '',
    };
  }, [artistUser]);

  const handleSelectReplyMessage = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    setMessageMenu(null);
    const draft = buildReplyDraftFromMessage(target);
    const topic = target.topic || (target.topic_id ? { id: target.topic_id, name: target.topic_name || 'スレッド' } : null);
    if (String(topic?.id || '') !== chatTopics.topicId) {
      chatTopics.pendingReplyDraft = draft;
      chatTopics.selectTopic(topic);
    }
    setReplyDraft(draft);
  }, [buildReplyDraftFromMessage, messageMenu, setReplyDraft]);

  useEffect(() => {
    setMessageMenu(null);
    setUnsendingMessageId('');
    setInlineCopyMessageId('');
  }, [threadId]);

  useEffect(() => {
    if (!messageMenu) return undefined;
    const handlePointerDown = () => setMessageMenu(null);
    const target = messagesContainerRef.current;
    window.addEventListener('click', handlePointerDown);
    target?.addEventListener('scroll', handlePointerDown, { passive: true });
    return () => {
      window.removeEventListener('click', handlePointerDown);
      target?.removeEventListener('scroll', handlePointerDown);
    };
  }, [messageMenu, messagesContainerRef]);

  useEffect(() => () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  return {
    canAnnouncementMessage,
    canCopyMessage,
    canReactMessage,
    canReplyMessage,
    canUnsendMessage,
    clearMessageLongPress,
    closeMessageMenu,
    handleCopyMessage,
    handleMessageContextMenu,
    handleMessageMenuReactionSelect,
    handleMessageTouchStart,
    handleOpenInlinePartialCopy,
    handleSelectReplyMessage,
    handleToggleAnnouncementMessage,
    handleUnsendMessage,
    inlineCopyMessageId,
    messageMenu,
    messageMenuRef,
    renderPartialCopyContent,
    unsendingMessageId,
  };
}
